<template>
  <div class="card" style="min-height: calc(100vh - 180px)">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          :showExtraBtn="true"
          :showAddNew="true"
          title="Production Plan"
          btnTitle="Add New"
          extraBtnTitle="List View"
          @onClickAddNewButton="onOpenAddModal"
          @onClickExtraBtn="navigateToListPage"
      />
    </div>

    <div class="px-2 pb-1 pt-1">
      <div class="row">
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Status</label>
          <v-select
              placeholder="Select Status"
              :options="statusList"
              v-model="statusFilter"
              label="name"
              :reduce="name => name.value"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Product/Item</label>
          <AsyncSelect
              placeholder="Select Product"
              v-model="product"
              :api-service="fetchProductDropdownList"
              :format-label="option => option.text"
              label="text"
              :additional-query="{ignore_stock: 1}"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Machine/Line</label>
          <AsyncSelect
              placeholder="Select Machine"
              v-model="machine"
              :api-service="fetchProductDropdownList"
              :format-label="option => option.text"
              label="text"
              :additional-query="{product_type: 'fixed_assets', ignore_stock: 1}"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3 d-flex align-items-end">
          <button
              style="min-width: 64px;"
              @click="refetchEvents"
              class="btn btn-primary btn-block waves-effect waves-float waves-light"
          >GO
          </button>
        </div>
      </div>
    </div>

    <div class="px-2 pb-1">
      <div class="d-flex flex-wrap gap-1 plan-legend">
        <span v-for="item in statusList" :key="item.value" class="legend-item">
          <span class="legend-dot" :style="legendStyle(item.value)"></span>{{ item.name }}
        </span>
      </div>
    </div>

    <div class="app-calendar overflow-hidden border mx-2 mb-2">
      <div class="row no-gutters">
        <div class="col position-relative">
          <div class="card shadow-none border-0 mb-0 rounded-0">
            <div class="card-body pb-0 plan-calendar">
              <CalendarComponent
                  :defaultView="'dayGridMonth'"
                  :calendarOptions="calendarOptions"
                  ref="calendarRef"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <Loader v-if="loading"/>

    <AddOrEditProductionPlanModal
        v-if="store.state.isModalOpen"
        :editData="editData"
        @onSubmit="onPlanSaved"
    />
    <ProductionPlanDetailModal
        v-if="store.state.isModalOpenTwo && singlePlan"
        :plan="singlePlan"
        @onEdit="onEditFromDetail"
        @onChanged="onPlanSaved"
    />
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import CalendarComponent from '@/components/atom/calendar/CalendarComponent.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import AddOrEditProductionPlanModal
  from '@/components/molecule/company/inventory/production-plan/AddOrEditProductionPlanModal.vue'
import ProductionPlanDetailModal
  from '@/components/molecule/company/inventory/production-plan/ProductionPlanDetailModal.vue'
import handlePurchase from '@/services/modules/purchase'
import handleDropdowns from '@/services/modules/dropdowns'
import { getCurrentDateTime, getProductionPlanStatusColor } from '@/services/utils/global'

const route = useRoute()
const router = useRouter()
const store = useStore()

const {
  fetchProductionPlanCalendar,
  fetchProductionPlanStatuses,
  fetchSingleProductionPlan,
  updateProductionPlanDateTime
} = handlePurchase()
const { fetchProductDropdownList } = handleDropdowns()

const loading = ref(false)
const calendarRef = ref(null)
const statusList = ref([])
const statusFilter = ref(null)
const product = ref(null)
const machine = ref(null)
const singlePlan = ref(null)
const editData = ref(null)

const calendarOptions = reactive({
  initialEvents: [],
  initialDate: route.query.anchor || undefined,
  editable: true,
  selectable: true,
  selectMirror: true,
  dayMaxEvents: 3,
  weekends: true,
  eventClick: handleEventClick,
  eventContent: renderEventContent,
  events: async function (info, successCallback, failureCallback) {
    let query = getQuery()
    query += '&start_date=' + info.startStr.substring(0, 19)
    query += '&end_date=' + info.endStr.substring(0, 19)

    await fetchProductionPlanCalendar(query).then((res) => {
      if (!res.status) {
        successCallback([])
        return
      }
      successCallback(res.data.map(mapPlanToEvent))
    }).catch((err) => {
      failureCallback(err)
    })
  },
  eventDrop: async function (info) {
    await persistPlanDateTime(info)
  },
  eventResize: async function (info) {
    await persistPlanDateTime(info)
  },
  datesSet: function (info) {
    syncRangeToUrl(info.view)
  }
})

function syncRangeToUrl(view) {
  const range = rangeFromView(view)
  if (!range) return
  if (isSameRange(range)) return

  const query = Object.assign({}, route.query)
  query.start = range.start
  query.end = range.end
  query.anchor = range.anchor
  router.replace({ path: route.path, query: query })
}

function isSameRange(range) {
  if (route.query.start !== range.start) return false
  if (route.query.end !== range.end) return false
  return route.query.anchor === range.anchor
}

function getQuery() {
  let query = '?company_id=' + route.params.companyId
  if (statusFilter.value) query += '&status=' + statusFilter.value
  if (product.value) query += '&product_id=' + product.value.id
  if (machine.value) query += '&product_machine_id=' + machine.value.id
  return query
}

function mapPlanToEvent(item) {
  const color = getProductionPlanStatusColor(item.status)
  const editable = !(item.production_count > 0)
  return {
    id: item.id,
    title: buildEventTitle(item),
    start: getCurrentDateTime(item.from_date_time),
    end: getCurrentDateTime(item.to_date_time),
    backgroundColor: color.bg,
    borderColor: color.border,
    textColor: color.text,
    editable: editable,
    extendedProps: {
      planNumber: item.plan_number,
      productName: item.product_name,
      machineName: item.machine_name,
      qty: item.qty,
      producedQty: item.produced_qty,
      status: item.status
    }
  }
}

function buildEventTitle(item) {
  if (item.title) return item.title
  return item.product_name
}

function renderEventContent(arg) {
  const props = arg.event.extendedProps
  const machineLine = props.machineName || 'No machine'
  const produced = Number(props.producedQty) || 0
  const color = getProductionPlanStatusColor(props.status)
  const style = `background:${color.bg};color:${color.text};border-left:3px solid ${color.border}`
  const html = `
    <div class="plan-event" style="${style}">
      <div class="plan-event-head">
        <span class="plan-event-no">${props.planNumber}</span>
        <span class="plan-event-qty" title="Produced / Planned">${produced} / ${props.qty}</span>
      </div>
      <div class="plan-event-title">${arg.event.title}</div>
      <div class="plan-event-meta">${machineLine}</div>
    </div>`
  return { html: html }
}

function legendStyle(status) {
  const color = getProductionPlanStatusColor(status)
  return `background:${color.bg};border-color:${color.border}`
}

async function persistPlanDateTime(info) {
  const payload = {
    company_id: route.params.companyId,
    from_date_time: formatEventDate(info.event.start),
    to_date_time: formatEventDate(info.event.end)
  }

  await updateProductionPlanDateTime(info.event.id, payload).then((res) => {
    if (!res.status) {
      info.revert()
      return
    }
  }).catch(() => {
    info.revert()
  })
}

function formatEventDate(date) {
  if (!date) return null
  return getCurrentDateTime(date).replace('T', ' ') + ':00'
}

async function handleEventClick(info) {
  loading.value = true
  await fetchSingleProductionPlan(info.event.id, `?company_id=${route.params.companyId}`).then((res) => {
    if (!res.status || !res.data) return
    singlePlan.value = res.data
    store.state.isModalOpenTwo = true
  }).catch(() => {
    singlePlan.value = null
  }).finally(() => {
    loading.value = false
  })
}

function getStatusList() {
  return fetchProductionPlanStatuses(`?company_id=${route.params.companyId}`).then((res) => {
    if (!res.status) return
    statusList.value = res.data
  })
}

function refetchEvents() {
  if (!calendarRef.value) return
  if (typeof calendarRef.value.getApi !== 'function') return
  calendarRef.value.getApi().refetchEvents()
}

function onOpenAddModal() {
  editData.value = null
  store.state.isModalOpen = true
}

function onEditFromDetail(plan) {
  editData.value = plan
  store.state.isModalOpenTwo = false
  store.state.isModalOpen = true
}

function onPlanSaved() {
  refetchEvents()
}

function rangeFromView(view) {
  if (!view) return null
  const lastDay = new Date(view.activeEnd)
  lastDay.setDate(lastDay.getDate() - 1)
  return {
    start: toDateOnly(view.activeStart),
    end: toDateOnly(lastDay),
    anchor: toDateOnly(view.currentStart)
  }
}

function getVisibleRange() {
  if (!calendarRef.value) return null
  if (typeof calendarRef.value.getApi !== 'function') return null
  return rangeFromView(calendarRef.value.getApi().view)
}

function toDateOnly(date) {
  return getCurrentDateTime(date).slice(0, 10)
}

function navigateToListPage() {
  const query = Object.assign({}, route.query)
  const range = getVisibleRange()

  if (range) {
    query.start = range.start
    query.end = range.end
  }

  router.push({
    name: 'production-plan-list',
    params: route.params,
    query: query
  })
}

onMounted(() => {
  store.state.isModalOpen = false
  store.state.isModalOpenTwo = false
  getStatusList()
})
</script>

<style scoped>
.plan-legend {
  font-size: 0.75rem;
  color: #6b7280;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-right: 0.75rem;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  border: 1px solid;
  display: inline-block;
}
</style>

<style>
.plan-calendar .fc {
  --fc-border-color: #eef0f4;
  --fc-today-bg-color: #f8fafc;
  font-size: 0.8rem;
}

.plan-calendar .fc .fc-toolbar-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #1f2937;
}

.plan-calendar .fc .fc-button {
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  border-radius: 6px;
  padding: 0.35rem 0.7rem;
  box-shadow: none;
}

.plan-calendar .fc .fc-button:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #111827;
}

.plan-calendar .fc .fc-button-primary:not(:disabled).fc-button-active {
  background: #6366f1;
  border-color: #6366f1;
  color: #fff;
}

.plan-calendar .fc .fc-col-header-cell {
  background: #fafbfc;
  padding: 0.5rem 0;
  font-weight: 600;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
}

.plan-calendar .fc .fc-daygrid-day-number {
  font-size: 0.75rem;
  color: #4b5563;
  padding: 0.35rem 0.45rem;
}

.plan-calendar .fc .fc-day-today .fc-daygrid-day-number {
  background: #6366f1;
  color: #fff;
  border-radius: 50%;
  min-width: 22px;
  text-align: center;
}

.plan-calendar .fc .fc-event {
  border-radius: 6px;
  border: 0;
  background: transparent;
  padding: 0;
  margin-bottom: 3px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  overflow: hidden;
}

.plan-calendar .fc .fc-event:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(16, 24, 40, 0.12);
}

.plan-calendar .fc .fc-daygrid-event {
  display: block;
  width: 100%;
}

.plan-calendar .fc .fc-daygrid-event-harness {
  width: 100%;
}

.plan-calendar .fc .fc-event .fc-event-main {
  width: 100%;
}

.plan-event {
  padding: 3px 6px;
  line-height: 1.25;
  border-radius: 6px;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

.plan-event-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
}

.plan-event-no {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  opacity: 0.75;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-event-qty {
  font-size: 0.65rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.55);
  border-radius: 8px;
  padding: 0 5px;
  white-space: nowrap;
  flex-shrink: 0;
}

.plan-event-title {
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.plan-event-meta {
  font-size: 0.65rem;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
